import { connect, FormikValues } from 'formik'
import { cloneDeep, get, isEmpty, startCase } from 'lodash'
import { Fragment, useCallback, useEffect } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import { Divider } from 'semantic-ui-react'
import styled from 'styled-components'
import { array, object, string } from 'yup'

import Loading from '@/components/Card/Loading'
import { FilterSelect, Select } from '@/components/Form'
import { FormLabel } from '@/components/StyledComponents'
import Toggle from '@/components/Toggle'
import VariableInput from '@/pages/SpaceAppBuilder/components/VariableInput/VariableInput'
import VariableAutoComplete from '@/pages/SpaceAppBuilder/components/VariableInputDropdown/AutoCompleteVariableInput'
import { getAppBuilderDataSourcesMappingData } from '@/pages/SpaceAppBuilder/FeaturesPage/selectors'
import { useInitialRender } from '@/pages/SpaceAppBuilder/FeaturesPage/useInitialRender'
import { useWorkflowFeature } from '@/pages/SpaceAppBuilder/FeaturesPage/WorkflowFeatureContext'
import {
  DataSourceMapping,
  DataSourceStepData,
  FieldFormats,
  FieldTypes,
  StepData,
  Variable
} from '@/pages/SpaceAppBuilder/utilities/interfaces'
import removeEmptyItems from '@/pages/SpaceAppBuilder/utilities/removeEmptyItems'
import { useAppSelector } from '@/store/hooks'

import messages from './messages'
import QueryParameters from './QueryParameters'

//<editor-fold desc="Interfaces">
type DataSourceOperationProps = {
  readOnly?: boolean
}
//</editor-fold>

//<editor-fold desc="Styled Components">
const StyledSelect = styled(Select)`
  && {
    .field__wrapper .field__input {
      color: ${props => (props.isplaceholder ? 'gray' : '#000000')};
    }
  }
`
const DataSourceActionContainer = styled.div<any>`
  .form__field {
    margin: 0.5rem 0 1rem;
  }
  min-height: 400px;
  margin-bottom: 50px;
`
const StyledDivider = styled(Divider)`
  margin-left: -20px;
  margin-right: -20px;
`
const StyledFilterSelect = styled(FilterSelect)`
  && {
    .ui.multiple.search.dropdown {
      .item {
        display: flex;
        flex-direction: row-reverse;
        justify-content: space-between;
        align-items: center;
        .ui.label {
          font-size: 0.75rem;
        }
      }
      > .text {
        margin-top: 8px;
        color: gray;
      }
    }
    .field__wrapper .ui.dropdown.multiple > a.label {
      font-size: 0.9rem;
      background-color: #ffffff;
      margin-top: 5px;
      border: none;
      color: #000000;
      > .delete.icon {
        margin-top: -1px;
      }
    }
  }
`

const StyledToggle = styled.div`
  padding-top: 15px;
  padding-bottom: 15px;
`

const StyledQueryParameters = styled(QueryParameters)`
  p {
    background-color: #0d71bb;
    .query-param-title {
      font-size: 0.5rem;
    }
  }
  .query-param-title {
    background-color: #1aa62a;
  }
`

const Description = styled.p`
  margin: 0px 0 20px;
  font-size: 0.9rem;
  color: #6c6c6c;
`

const LoaderOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(243, 243, 244, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
`
//</editor-fold>

const getValidationSchema = (intl: IntlShape, formObject: any) => {
  // input.path.id
  if (Object.keys(formObject?.input?.path ?? {})?.includes('id')) {
    return {
      input: object().shape({
        path: object().shape({
          id: string().trim().required(intl.formatMessage(messages.dataSourceIdFieldRequired))
        })
      })
    }
  }

  // if the 'where' param is present in our query object then it is required per the docs, however it only needs a value in the form if there's no filter
  const queryKeys = Object.keys(formObject?.input?.query ?? {})
  if (queryKeys?.includes('where') || queryKeys?.includes('params')) {
    return {
      input: object().shape({
        query: object().shape({
          params: array().of(
            object()
              .shape({
                key: string().trim().required(intl.formatMessage(messages.queryNameRequired)),
                value: string().trim().required(intl.formatMessage(messages.queryValueRequired))
              })
              .required()
          ),
          where: string().when('params', {
            is: params => !params,
            then: string().required(intl.formatMessage(messages.queryStringRequired))
          })
        })
      })
    }
  }
}

const getInitialValues = (stepData: DataSourceStepData): StepData => ({
  dataSourceKey: stepData.dataSourceKey,
  endpoint: stepData.path + ':' + stepData.method,
  path: stepData.path,
  input: get(stepData, 'input', {
    path: {},
    query: {
      fields: [],
      filters: [],
      params: []
    },
    body: {}
  })
})

const getNetworkRequestObject = (data: { triggerData: any; stepData: any }) => {
  const updatedValues: any = cloneDeep(data)
  const { stepData } = data
  if (isEmpty(stepData)) return data

  const endpoint = stepData.endpoint.split(':')
  updatedValues.stepData = {
    ...stepData,
    path: endpoint[0],
    method: endpoint[1],
    endpoint: undefined
  }

  // TODO Work this out when we enable the pagination
  // // Add isPaginated as a flag (it isn't an input in formik but needs to be added to the request)
  // if (originalConfiguration && isPaginatedEnabled) {
  //   stepData.isPaginated = originalConfiguration.stepData?.isPaginated
  // }

  return removeEmptyItems(updatedValues)
}

const getDataSourceOperationHelpUrl = 'https://help.workgrid.com/s/article/Data-Source-Operations'

const DataSourceOperation = ({ formik, readOnly = false }: DataSourceOperationProps & FormikValues) => {
  const intl = useIntl()

  const { stepData } = formik.values
  const dataSourceMappingFetchState = useAppSelector(getAppBuilderDataSourcesMappingData)
  const initialRender = useInitialRender()
  const { getVariablesForStep } = useWorkflowFeature()
  const variables = getVariablesForStep()

  const hasActions = !!dataSourceMappingFetchState.dataSourcesMapping[stepData?.dataSourceKey]?.actions

  useEffect(() => {
    if (!initialRender) {
      const endpoint = stepData.endpoint.split(':')
      const operation =
        !hasActions &&
        dataSourceMappingFetchState.dataSourcesMapping[stepData.dataSourceKey].find(
          (ds: DataSourceMapping) => ds.path === endpoint[0] && ds.method === endpoint[1]
        )
      if (operation) {
        formik.setFieldValue(`name`, operation.endpointName)
        formik.setFieldValue(`stepData.input`, {
          path: {},
          query: {
            fields: [],
            filters: []
          },
          body: {}
        })
        formik.setFieldValue(`stepData.path`, operation.path)
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [stepData.endpoint])

  const selectedOperation =
    stepData.endpoint &&
    !hasActions &&
    dataSourceMappingFetchState.dataSourcesMapping[stepData.dataSourceKey]?.find(
      (ds: DataSourceMapping) => ds.path + ':' + ds.method === stepData.endpoint
    )

  const operationHasWhereParam = selectedOperation?.parameters?.find((param: any) => param.name === 'where')

  const isNumeric = (num: any) => typeof num === 'number' || !Number.isNaN(Number(num))

  const onInputChanged = useCallback(
    (name: string, value: string, format: FieldFormats) => {
      if (format === FieldFormats.NUMBER && isNumeric(value) && value?.trim() !== '') {
        formik.setFieldValue(name, Number(value))
      } else {
        formik.setFieldValue(name, value)
      }
    },
    [formik]
  )

  const onSelectVariable = (variable: Variable, formikName: string) => {
    formik.setFieldValue(formikName, variable.name)
  }

  const renderConfigurationInput = (paramConfig: any) => {
    let field
    const name = paramConfig.name || paramConfig['x-displayName'] || paramConfig['x-alias'] || paramConfig.propertyName
    switch (paramConfig.type) {
      case FieldTypes.INTEGER:
      case FieldTypes.NUMBER:
      case FieldTypes.STRING: {
        const getFieldObject = () => {
          return Object.keys(selectedOperation.response.model.properties).map(dataSourceObject => ({
            key: dataSourceObject,
            value: dataSourceObject,
            label: selectedOperation.response.model.properties[dataSourceObject].type || FieldTypes.OBJECT,
            text: startCase(dataSourceObject)
          }))
        }
        if (paramConfig.name === 'fields') {
          field = (
            <>
              <FormLabel>{intl.formatMessage(messages.fields)}</FormLabel>
              <span className="field__description">{intl.formatMessage(messages.fieldsDescription)}</span>
              <StyledFilterSelect
                disabled={readOnly}
                name={`stepData.input.${paramConfig.in}.${paramConfig.name}`}
                multiple
                options={getFieldObject()}
                className="fill-space"
              />
            </>
          )
        } else {
          let format = paramConfig?.format || FieldFormats.TEXT
          const numericFields = [FieldFormats.INT, FieldFormats.DOUBLE, FieldFormats.FLOAT]
          if (numericFields.some((numericField: string) => paramConfig?.format?.startsWith(numericField))) {
            format = FieldFormats.NUMBER
          }
          if (format === FieldFormats.TEXT || format === FieldFormats.NUMBER || format === FieldFormats.DATE_TIME) {
            return (
              <VariableInput
                name={`stepData.input.${paramConfig.in}.${paramConfig.name}`}
                label={startCase(name)}
                onChange={value =>
                  onInputChanged(`stepData.input.${paramConfig.in}.${paramConfig.name}`, value, format)
                }
                multiline={true}
                placeholder={paramConfig && paramConfig['x-samplevalue']}
                description={paramConfig?.description?.length > 100 ? '' : paramConfig.description}
                tooltip={paramConfig?.description?.length > 100 ? paramConfig.description : ''}
                validVariableTypes={[
                  FieldTypes.STRING,
                  FieldTypes.BOOLEAN,
                  FieldTypes.INTEGER,
                  FieldTypes.NUMBER,
                  FieldTypes.ANY
                ]}
                variables={variables}
                required={paramConfig?.required || paramConfig['x-required']}
                readOnly={readOnly}
              />
            )
          }
        }
        break
      }
      case FieldTypes.BOOLEAN: {
        field = (
          <StyledToggle>
            <Toggle
              label={startCase(name)}
              disabled={readOnly}
              name={`stepData.input.${paramConfig.in}.${paramConfig.name}`}
              checked={stepData.input[paramConfig.in][paramConfig.name]}
              formik={formik}
            ></Toggle>
          </StyledToggle>
        )
        break
      }
      case FieldTypes.OBJECT: {
        field = Object.keys(paramConfig.properties).map((property: any) => (
          <Fragment key={paramConfig.in + property}>
            {renderConfigurationInput({
              in: paramConfig.in,
              name: property,
              ...paramConfig.properties[property]
            })}
          </Fragment>
        ))
        break
      }
      case FieldTypes.ARRAY: {
        field = (
          <VariableAutoComplete
            readOnly={readOnly}
            name={`stepData.input.${paramConfig.in}.${paramConfig.name}`}
            label={startCase(name)}
            variables={variables}
            validVariableTypes={[FieldTypes.ARRAY]}
            placeholder={get(paramConfig, 'x-samplevalue', name)}
            // @ts-expect-error
            onSelectOption={onSelectVariable}
            showValidationMessage={true}
            required={paramConfig?.required || paramConfig['x-required']}
          />
        )
      }
    }
    return field
  }

  const isWhereRequired = (selectedOperation: any) => {
    const whereParam = selectedOperation?.parameters.find((param: any) => param.name === 'where')
    return whereParam && whereParam.required === true
  }

  return (
    <DataSourceActionContainer data-testid="DataSourceActionContainer">
      <Description>{intl.formatMessage(messages.dataSourceActionDescription)}</Description>
      {dataSourceMappingFetchState.isLoading ? (
        <LoaderOverlay>
          <Loading
            isLoading={dataSourceMappingFetchState.isLoading}
            loadingMessage={intl.formatMessage(messages.loading)}
          />
        </LoaderOverlay>
      ) : (
        <>
          <StyledSelect
            disabled={readOnly}
            label={intl.formatMessage(messages.whatAction)}
            name={`stepData.endpoint`}
            variant={''}
            isplaceholder={!stepData.endpoint}
          >
            <option value={''} disabled selected>
              {intl.formatMessage(messages.selectOperation)}
            </option>
            {!hasActions &&
              dataSourceMappingFetchState.dataSourcesMapping[stepData.dataSourceKey]?.map(
                (dataSource: DataSourceMapping) => (
                  <option value={dataSource.path + ':' + dataSource.method} key={dataSource.endpointName}>
                    {dataSource.endpointName}
                  </option>
                )
              )}
          </StyledSelect>
          <StyledDivider />
          {!!selectedOperation && (
            <>
              {selectedOperation.parameters
                .filter((param: any) => param.name !== 'where')
                .map((param: any) => (
                  <Fragment key={param.name}>{renderConfigurationInput(param)}</Fragment>
                ))}
              {!!operationHasWhereParam && (
                <StyledQueryParameters
                  formik={formik}
                  readOnly={readOnly}
                  operation={'data-source-operation'}
                  variables={variables}
                  queryParamPath={'stepData.input.query.params'}
                  isQueryParamRequired={isWhereRequired(selectedOperation)}
                />
              )}
            </>
          )}
        </>
      )}
    </DataSourceActionContainer>
  )
}

export default connect(DataSourceOperation)

export { getValidationSchema, getInitialValues, getNetworkRequestObject, getDataSourceOperationHelpUrl }
